import ConfirmationNumberRoundedIcon from '@mui/icons-material/ConfirmationNumberRounded';
import PeopleAltRoundedIcon from '@mui/icons-material/PeopleAltRounded';
import SchoolRoundedIcon from '@mui/icons-material/SchoolRounded';
import {
  Box,
  Divider,
  Grid,
  Typography,
  useMediaQuery,
  useTheme,
} from '@mui/material';
import { useEffect, useState } from 'react';
import { useOutletContext } from 'react-router-dom';
import AdminApi from '../../../apis/AdminApi';
import ChatBox from '../../../components/ChatBox/ChatBox';
import LoadingCircle from '../../../components/LoadingCircle/LoadingCircle';
import UnexpectedError from '../../../components/UnexpectedError/UnexpectedError';
import { AdminCountDto } from '../../../generated-types/adminCountDto';
import useApi from '../../../lib/useApi';
import AdminViewCard from '../Components/AdminViewCard';
import CountRow from './components/CountRow';
import UnreadMessagesCard from './components/UnreadMessagesCard';

/**
 * The home subview of the admin dashboard
 */
const HomeSubview = () => {
  const spacing = 2;
  const { unreadMessages } = useOutletContext<{ unreadMessages: number }>();
  const [count, setCount] = useState<AdminCountDto>({
    completedCourses: 0,
    licensesSold: 0,
    totalUsers: 0,
  });
  const [loadingCount, setLoadingCount] = useState(true);
  const [error, setError] = useState(false);
  const theme = useTheme();
  const isMdExact = useMediaQuery(theme.breakpoints.only('md'), {
    defaultMatches: true,
  });
  const adminApi = useApi(AdminApi);

  useEffect(() => {
    setLoadingCount(true);
    adminApi
      .GetCounts()
      .then(setCount)
      .catch(() => setError(true))
      .finally(() => setLoadingCount(false));
  }, [adminApi]);

  return (
    <Box padding="1em">
      {error ? (
        <Box height="500px">
          <UnexpectedError errorContext="dashboard content" />
        </Box>
      ) : (
        <Grid container spacing={spacing}>
          <Grid item xs={12} sm={9} md={12} lg={9}>
            <AdminViewCard>
              <Box
                padding={2}
                display="flex"
                alignItems="center"
                justifyContent="center"
                height="100%"
              >
                <Typography
                  variant="h3"
                  textAlign="center"
                  fontWeight={500}
                  fontSize="3.5em"
                >
                  Admin Dashboard
                </Typography>
              </Box>
            </AdminViewCard>
          </Grid>
          {!isMdExact && (
            <Grid item xs={12} sm={3}>
              <UnreadMessagesCard unreadMessages={unreadMessages} />
            </Grid>
          )}
          <Grid container item xs={12} md={6} spacing={spacing}>
            {isMdExact && (
              <Grid item xs={12}>
                <UnreadMessagesCard unreadMessages={unreadMessages} />
              </Grid>
            )}
            <Grid item xs={12}>
              <AdminViewCard>
                <Box position="relative" padding="1em">
                  <CountRow
                    count={count.totalUsers}
                    icon={<PeopleAltRoundedIcon />}
                    title="Total Users"
                  />
                  <Divider flexItem />
                  <CountRow
                    count={count.completedCourses}
                    icon={<SchoolRoundedIcon />}
                    title="Completed Courses"
                  />
                  <Divider flexItem />
                  <CountRow
                    count={count.licensesSold}
                    icon={<ConfirmationNumberRoundedIcon />}
                    title="Licenses Sold"
                  />
                  <LoadingCircle isLoading={loadingCount} />
                </Box>
              </AdminViewCard>
            </Grid>
            <Grid item xs={12}>
              <AdminViewCard>
                <Box height="200px">Expiring Subscriptions</Box>
              </AdminViewCard>
            </Grid>
          </Grid>
          <Grid item xs={12} md={6}>
            <ChatBox
              converse={(msg) => {
                console.log(msg);
                return new Promise<string>(() => 'return message');
              }}
              isLoading={false}
            />
          </Grid>
        </Grid>
      )}
    </Box>
  );
};

export default HomeSubview;
